import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import Tooltip from '@mui/material/Tooltip';
import { projects } from '../data/portfolio';
import { Project } from '../types';
import './Projects.css';
import ImageSlider from './ImageSlider';

// Stockage global des projets déjà animés
const animatedProjects = new Set<number>();

const Projects: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const categories = ['all', 'web', 'fullstack', 'frontend', 'backend'];

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(project => project.category === filter);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
    const ref = useRef(null);
    const [hasAnimated, setHasAnimated] = useState(animatedProjects.has(project.id));
    const isInView = useInView(ref, {
      once: true,
      margin: "-100px"
    });

    useEffect(() => {
      if (isInView && !hasAnimated) {
        animatedProjects.add(project.id);
        setHasAnimated(true);
      }
    }, [isInView, hasAnimated, project.id]);

    return (
      <motion.div
        ref={ref}
        className="project-card"
        initial={hasAnimated ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.9 }}
        animate={isInView || hasAnimated ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.9 }}
        transition={{
          duration: 0.6,
          delay: hasAnimated ? 0 : index * 0.15,
          ease: "easeOut"
        }}
        whileHover={{
          y: -10,
          boxShadow: "0 15px 30px rgba(0,0,0,0.15)",
          scale: 1.02
        }}
        onClick={() => setSelectedProject(project)}
      >
        <motion.div
          className="project-image"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          <img src={project.images[0]} alt={project.title} />
          <motion.div
            className="project-overlay"
            whileHover={{ backgroundColor: "rgba(0,0,0,0.7)" }}
          >
            <motion.div
              className="project-actions"
              whileHover={{ scale: 1.1 }}
            >
              <motion.button
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Voir plus
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>

        <div className="project-content">
          <div className="project-category">{project.category}</div>
          <h3 className="project-title">{project.title}</h3>
          <p className="project-description">{project.description}</p>

          <div className="project-technologies">
            {project.technologies.slice(0, 3).map((tech) => (
              <span key={tech} className="tech-tag">{tech}</span>
            ))}
            {project.technologies.length > 3 && (
              <span className="tech-more">+{project.technologies.length - 3}</span>
            )}
          </div>

          <div className="project-links">
            {project.liveUrl && (
              <Tooltip title="Voir le site" arrow placement="top">
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fas fa-external-link-alt"></i>
                </a>
              </Tooltip>
            )}
            {project.githubUrl && (
              <Tooltip title="Voir le code" arrow placement="top">
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fab fa-github"></i>
                </a>
              </Tooltip>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const ProjectModal: React.FC<{ project: Project; onClose: () => void }> = ({ project, onClose }) => (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{
          duration: 0.5,
          ease: "easeOut"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        <div className="modal-header">
          <h2>{project.title}</h2>
          <div className="project-category">{project.category}</div>
        </div>

        <div className="modal-body">
          <div className="project-images">
            <ImageSlider images={project.images} title={project.title} />
          </div>

          <div className="project-details">
            <p className="project-long-description">{project.longDescription}</p>

            <div className="project-technologies-full">
              <h4>Technologies utilisées :</h4>
              <div className="tech-list">
                {project.technologies.map((tech) => (
                  <span key={tech} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>

            <div className="project-actions-modal">
              {project.liveUrl && (
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  <i className="fas fa-external-link-alt"></i>
                  Voir le site
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  <i className="fab fa-github"></i>
                  Code source
                </a>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <section id="projects" className="projects section-padding">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Mon Portfolio
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Projets personnels et de formation — mes missions professionnelles restent confidentielles, mais je suis disponible pour échanger sur les technologies utilisées et les défis techniques rencontrés.
          </motion.p>
        </motion.div>

        {/*
        <motion.div
          className="filter-buttons"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {categories.map((category, index) => (
            <motion.button
              key={category}
              className={`filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{
                scale: 1.05,
                backgroundColor: filter === category ? "" : "rgba(0,0,0,0.05)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              {category === 'all' ? 'Tous' : category}
            </motion.button>
          ))}
        </motion.div>
        */

        }


        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;